/**
 * Утилиты для безопасной обработки ошибок.
 *
 * Использование:
 * ```typescript
 * catch (error: unknown) {
 *   const message = getErrorMessage(error);
 *   toast.error(message);
 * }
 * ```
 */

/** Структура ответа axios при ошибке */
interface AxiosErrorResponse {
  data?: { error?: string; message?: string }
  status?: number
}

/** Проверяет, является ли значение объектом с полем response (axios error) */
export function isAxiosError(error: unknown): error is { response: AxiosErrorResponse; message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as Record<string, unknown>).response === 'object'
  )
}

/**
 * Извлекает сообщение ошибки из любого типа ошибки.
 *
 * Приоритет:
 * 1. response.data.error (axios /api ошибки)
 * 2. response.data.message
 * 3. error.message (стандартные Error)
 * 4. строковое представление
 * 5. fallback
 */
export function getErrorMessage(error: unknown, fallback = 'Произошла неизвестная ошибка'): string {
  if (isAxiosError(error)) {
    return error.response.data?.error || error.response.data?.message || error.message || fallback
  }

  if (error instanceof Error) {
    return error.message || fallback
  }

  if (typeof error === 'string') {
    return error
  }

  return fallback
}

/**
 * Извлекает HTTP-статус из ошибки.
 * Возвращает undefined, если это не HTTP-ошибка.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response.status
  }
  return undefined
}

/**
 * Проверяет, является ли ошибка ошибкой авторизации (401).
 */
export function isAuthError(error: unknown): boolean {
  return getErrorStatus(error) === 401
}

/**
 * Проверяет, является ли ошибка ошибкой "не найдено" (404).
 */
export function isNotFoundError(error: unknown): boolean {
  return getErrorStatus(error) === 404
}

/**
 * Логирует ошибку с детализацией по типу (axios / network / setup).
 * Используется в catch-блоках, где нужно подробное логирование.
 */
export function logError(context: string, error: unknown): void {
  console.error(context, error)

  if (isAxiosError(error)) {
    console.error(`Server responded with status ${error.response.status}:`)
    console.error('Response data:', error.response.data)
  } else if (error instanceof Error) {
    console.error('Error message:', error.message)
  }
}
